import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Tooltip
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  showSuccessMessage,
  showLoader,
  dismissLoader
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import partnerAPI from "../../../../services/channelPartnerService";

export default function ChannelPartnerUsers({ partnerId }) {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    userName: "",
    password: "",
    email: "",
    phone: "",
    roleCode: "CHANNEL_PARTNER_USER"
  });

  // Fetch users on component mount
  useEffect(() => {
    if (!partnerId) return;
    fetchUsers();
  }, [partnerId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log("[ChannelPartnerUsers] Fetching users for partner:", partnerId);
      const response = await partnerAPI.get(`/api/channel-partners/${partnerId}/users`);

      if (Array.isArray(response.data)) {
        console.log("[ChannelPartnerUsers] Fetched users:", response.data);
        setUsers(response.data);
      }
    } catch (error) {
      console.error("[ChannelPartnerUsers] Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setFormData({
      name: "",
      userName: "",
      password: "",
      email: "",
      phone: "",
      roleCode: "CHANNEL_PARTNER_USER"
    });
    setAddDialogOpen(true);
  };

  const handleSaveNewUser = async () => {
    if (!formData.name || !formData.userName || !formData.password || !formData.email || !formData.phone) {
      dispatch(showSuccessMessage("Please fill all required fields"));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      dispatch(showSuccessMessage("Please enter a valid email address"));
      return;
    }

    dispatch(showLoader());
    try {
      console.log("[ChannelPartnerUsers] Adding user:", formData);
      const response = await partnerAPI.post(
        `/api/channel-partners/${partnerId}/users`,
        formData
      );

      console.log("[ChannelPartnerUsers] User added successfully:", response.data);
      dispatch(showSuccessMessage("User added successfully!"));
      setAddDialogOpen(false);

      // Refresh users list
      await fetchUsers();
    } catch (error) {
      console.error("[ChannelPartnerUsers] Error adding user:", error);
      dispatch(showSuccessMessage(error.response?.data?.message || "Error adding user"));
    } finally {
      dispatch(dismissLoader());
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || "",
      userName: user.username || "",
      password: "",
      email: user.email || "",
      phone: user.phone || "",
      roleCode: user.roles && user.roles.length > 0 ? user.roles[0].code : "CHANNEL_PARTNER_USER"
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUserRole = async () => {
    if (!selectedUser) {
      dispatch(showSuccessMessage("Please select a user to update"));
      return;
    }

    if (!formData.roleCode) {
      dispatch(showSuccessMessage("Please select a role"));
      return;
    }

    dispatch(showLoader());
    try {
      console.log("[ChannelPartnerUsers] Updating user role:", formData.roleCode);
      const response = await partnerAPI.put(
        `/api/channel-partners/${partnerId}/users/${selectedUser.id}/role`,
        { roleCode: formData.roleCode }
      );

      console.log("[ChannelPartnerUsers] User role updated successfully:", response.data);
      dispatch(showSuccessMessage("User role updated successfully!"));
      setEditDialogOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error("[ChannelPartnerUsers] Error updating user role:", error);
      dispatch(showSuccessMessage(error.response?.data?.message || "Error updating user role"));
    } finally {
      dispatch(dismissLoader());
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user from the channel partner?")) {
      return;
    }

    dispatch(showLoader());
    try {
      console.log("[ChannelPartnerUsers] Deleting user:", userId);
      await partnerAPI.delete(`/api/channel-partners/${partnerId}/users/${userId}`);

      dispatch(showSuccessMessage("User removed successfully!"));

      // Refresh users list
      await fetchUsers();
    } catch (error) {
      console.error("[ChannelPartnerUsers] Error deleting user:", error);
      dispatch(showSuccessMessage(error.response?.data?.message || "Error removing user"));
    } finally {
      dispatch(dismissLoader());
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const action = currentStatus ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    dispatch(showLoader());
    try {
      console.log(`[ChannelPartnerUsers] ${action} user:`, userId);
      const endpoint = currentStatus
        ? `/api/channel-partners/${partnerId}/users/${userId}/deactivate`
        : `/api/channel-partners/${partnerId}/users/${userId}/activate`;

      const response = await partnerAPI.post(endpoint);

      dispatch(showSuccessMessage(`User ${action}d successfully!`));

      // Refresh users list
      await fetchUsers();
    } catch (error) {
      console.error(`[ChannelPartnerUsers] Error ${action}ing user:`, error);
      dispatch(showSuccessMessage(error.response?.data?.message || `Error ${action}ing user`));
    } finally {
      dispatch(dismissLoader());
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setFormData({
      name: "",
      userName: "",
      password: "",
      email: "",
      phone: "",
      roleCode: "CHANNEL_PARTNER_USER"
    });
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setFormData({
      name: "",
      userName: "",
      password: "",
      email: "",
      phone: "",
      roleCode: "CHANNEL_PARTNER_USER"
    });
  };

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <h3>Channel Partner Users ({users.length})</h3>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
          color="primary"
        >
          Add User
        </Button>
      </Box>

      {users.length === 0 ? (
        <Alert severity="info">No users added yet. Click "Add User" to create one.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><strong>Full Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name || "N/A"}</TableCell>
                  <TableCell>{user.email || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.roles && user.roles.length > 0 ? user.roles[0].code : "USER"}
                      size="small"
                      color={user.isAdmin ? "error" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={user.isActive ? "Click to deactivate" : "Click to activate"}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleUserStatus(user.id, user.isActive)}
                        color={user.isActive ? "success" : "error"}
                      >
                        {user.isActive ? (
                          <CheckCircleIcon fontSize="small" />
                        ) : (
                          <CancelIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Chip
                      label={user.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={user.isActive ? "success" : "error"}
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(user)}
                      title="Edit user"
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Remove user"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add User to Channel Partner</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Full Name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            placeholder="e.g., John Doe"
          />
          <TextField
            fullWidth
            label="Username"
            type="text"
            value={formData.userName}
            onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
            margin="normal"
            placeholder="e.g., johndoe"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            placeholder="e.g., john@example.com"
          />
          <TextField
            fullWidth
            label="Phone"
            type="text"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            margin="normal"
            placeholder="e.g., +1234567890"
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
            placeholder="Enter a secure password"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.roleCode}
              onChange={(e) => setFormData({ ...formData, roleCode: e.target.value })}
              label="Role"
            >
              <MenuItem value="CHANNEL_PARTNER_ADMIN">Admin</MenuItem>
              <MenuItem value="CHANNEL_PARTNER_USER">User</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleSaveNewUser} variant="contained" color="primary">
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Name, username, email, and phone are read-only. To change them, remove this user and create a new one.
          </Alert>
          <TextField
            fullWidth
            label="Full Name"
            type="text"
            value={formData.name}
            disabled
            margin="normal"
          />
          <TextField
            fullWidth
            label="Username"
            type="text"
            value={formData.userName}
            disabled
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            disabled
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone"
            type="text"
            value={formData.phone}
            disabled
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.roleCode}
              onChange={(e) => setFormData({ ...formData, roleCode: e.target.value })}
              label="Role"
            >
              <MenuItem value="CHANNEL_PARTNER_ADMIN">Admin</MenuItem>
              <MenuItem value="CHANNEL_PARTNER_USER">User</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateUserRole} variant="contained" color="primary">
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
